// frontend/src/hooks/games/useGameSessionTracking.ts

import { useCallback, useEffect, useRef, useState } from 'react';
import { recordGameSession, GameStats } from '../../services/games/gameStatsService';
import { awardXp } from '../../services/games/xpService';
import { checkAndUnlockAchievements } from '../../services/games/achievementsService';
import { publishAchievementUnlocks } from '../../services/games/achievementBus';
import { AchievementDefinition, AchievementSessionContext } from '../../config/achievements';

export interface RecordCompletionInput {
  /** Bu round'da elde edilen skor (varsa). */
  score?: number;
  /** Toplayıcı sayaçlara eklenecek delta'lar (örn. { fruitsEaten: 3 }). */
  cumulativeDeltas?: Record<string, number>;
  /** Bu round için kazanılacak XP miktarı (oyuna özel formülle sayfa tarafından hesaplanır). */
  xpAmount?: number;
  /** Başarım kontrolleri için bu round'a özel gerçekler (örn. { isPerfect: true }). */
  session?: AchievementSessionContext;
}

export interface RecordCompletionResult {
  xpAwarded: number;
  stats: GameStats;
  newAchievements: AchievementDefinition[];
}

/**
 * Her oyun sayfası, round bittiğinde (isGameOver/isComplete/winner) bu hook'un
 * `recordCompletion` fonksiyonunu bir kere çağırır. Bu hook, ilgili oyunun
 * gameplay hook'una (useSnakeGame, useMemoryGame, vb.) hiç dokunmaz.
 */
export function useGameSessionTracking(gameId: string) {
  const roundStartRef = useRef<number>(Date.now());
  const [lastXpEarned, setLastXpEarned] = useState<number | null>(null);

  // Sayfa değişirse (farklı bir oyuna geçilirse) zamanlayıcıyı sıfırla.
  useEffect(() => {
    roundStartRef.current = Date.now();
  }, [gameId]);

  const resetRoundTimer = useCallback(() => {
    roundStartRef.current = Date.now();
  }, []);

  const recordCompletion = useCallback((input: RecordCompletionInput): RecordCompletionResult => {
    const playTimeMs = Date.now() - roundStartRef.current;
    roundStartRef.current = Date.now(); // bir sonraki round için sıfırla

    const stats = recordGameSession(gameId, {
      score: input.score,
      playTimeMs,
      cumulativeDeltas: input.cumulativeDeltas,
    });

    let xpAwarded = 0;
    if (input.xpAmount && input.xpAmount > 0) {
      const result = awardXp({ gameId, amount: input.xpAmount });
      xpAwarded = result.amount;
    }
    setLastXpEarned(xpAwarded);

    const newAchievements = checkAndUnlockAchievements(gameId, stats, input.session ?? {});
    if (newAchievements.length > 0) publishAchievementUnlocks(newAchievements);

    return { xpAwarded, stats, newAchievements };
  }, [gameId]);

  return { recordCompletion, resetRoundTimer, lastXpEarned };
}
